import { useEffect, useRef } from "react"
import { useQueryClient } from "@tanstack/react-query"

import { ConfigQuery } from "./createOrderApi"
import { CreateOrderDialog } from "./CreateOrderDialog"
import { CreateOrderDialogMode } from "./createOrderTypes"
import { useCreateOrderUser } from "./hooks/useCreateOrderUser"
import { useOrderDialogUrlState } from "./hooks/useOrderDialogUrlState"
import { invalidateOrderDataOnDialogClose } from "./orderDataInvalidation"

export function CreateOrderDialogHost() {
    const { isOpen, config, closeDialog } = useOrderDialogUrlState()
    const queryClient = useQueryClient()

    // The dialog can close without its close handler ever running (browser/mouse back, route
    // change), so refresh on the open -> closed transition instead of from inside that handler.
    // This host is always mounted (Layout), so the transition is observable from here.
    const wasOpenRef = useRef(isOpen)
    useEffect(() => {
        if (wasOpenRef.current && !isOpen) {
            invalidateOrderDataOnDialogClose(queryClient)
        }
        wasOpenRef.current = isOpen
    }, [isOpen, queryClient])

    if (!isOpen) return null

    return <CreateOrderDialogHostOpen config={config} closeDialog={closeDialog} />
}

type CreateOrderDialogHostOpenProps = Readonly<{
    config: ReturnType<typeof useOrderDialogUrlState>["config"]
    closeDialog: () => void
}>

function CreateOrderDialogHostOpen({ config, closeDialog }: CreateOrderDialogHostOpenProps) {
    const mode = config.mode ?? CreateOrderDialogMode.CREATE
    const { onBehalf, setOnBehalf } = useCreateOrderUser({ mode })

    const queryClient = useQueryClient()

    // Start every dialog session with a clean extra-services cache (cleared on open/mount).
    // This covers every way the previous session was closed — UI button, browser/mouse back,
    // route change — so reopening with the same delivery date/time always refetches (async)
    // instead of serving a synchronous cache hit, which collides with the form reinitialize/
    // sync cycle and flickers the drawer/cart. Must remove (not invalidate) — invalidate keeps
    // the data and still serves it synchronously.
    useEffect(() => {
        queryClient.removeQueries({ queryKey: [ConfigQuery.EXTRA_SERVICES_FOR_USER] })
    }, [queryClient])

    return (
        <CreateOrderDialog
            open
            orderId={config.orderId}
            mode={mode}
            dialogType={config.dialogType}
            activeOrderTab={config.activeOrderTab}
            flowConfig={config.flowConfig}
            onClose={() => closeDialog()}
            isOnBehalfCompleted={onBehalf}
            setIsOnBehalfCompleted={setOnBehalf}
        />
    )
}
